import React, { useState, useEffect, useCallback } from 'react';
import { Shield, ShieldCheck, Loader2, Copy, Check, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SecurityTab = ({ authToken }) => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState(null); // { secret, qr_data_url }
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableMode, setDisableMode] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [copied, setCopied] = useState(false);

  const headers = { 'Authorization': `Bearer ${authToken}` };

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/totp/status`, { headers });
      if (res.ok) setEnabled((await res.json()).enabled);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, [authToken]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const startSetup = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/totp/setup/start`, { method: 'POST', headers });
      const data = await res.json();
      if (res.ok) { setSetup(data); setBackupCodes(null); }
      else toast.error(data.detail || 'Failed to start setup');
    } catch (e) { toast.error('Failed to start setup'); }
  };

  const verifySetup = async () => {
    if (code.trim().length < 6) { toast.error('Enter the 6-digit code'); return; }
    setVerifying(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/totp/setup/verify`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setBackupCodes(data.backup_codes);
        setSetup(null); setCode(''); setEnabled(true);
        toast.success('Two-factor authentication enabled');
      } else toast.error(data.detail || 'Invalid code');
    } catch (e) { toast.error('Verification failed'); } finally { setVerifying(false); }
  };

  const disable2FA = async () => {
    if (disableCode.trim().length < 6) { toast.error('Enter a current code'); return; }
    try {
      const res = await fetch(`${API_URL}/api/admin/totp/disable`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: disableCode.trim() })
      });
      const data = await res.json();
      if (res.ok) { setEnabled(false); setDisableMode(false); setDisableCode(''); toast.success('2FA disabled'); }
      else toast.error(data.detail || 'Invalid code');
    } catch (e) { toast.error('Failed to disable'); }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-white/50 py-10"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;
  }

  return (
    <div className="max-w-2xl" data-testid="security-tab">
      <div className="border border-white/10 p-6 bg-white/[0.02]">
        <div className="flex items-start gap-4 mb-6">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-white/5 border border-white/10'}`}>
            {enabled ? <ShieldCheck className="w-6 h-6 text-emerald-400" /> : <Shield className="w-6 h-6 text-white/50" />}
          </div>
          <div>
            <h3 className="text-white font-medium text-lg">Two-Factor Authentication</h3>
            <p className="text-white/50 text-sm mt-1">
              {enabled ? 'Your admin login is protected with an authenticator app.' : 'Add a second layer of security using Google Authenticator or Authy.'}
            </p>
            <span className={`inline-block mt-2 px-2 py-0.5 text-xs ${enabled ? 'bg-emerald-500/15 text-emerald-300' : 'bg-white/10 text-white/50'}`} data-testid="2fa-status">
              {enabled ? 'Active' : 'Not enabled'}
            </span>
          </div>
        </div>

        {/* Enabled state */}
        {enabled && !disableMode && (
          <button data-testid="disable-2fa-btn" onClick={() => setDisableMode(true)} className="px-5 py-2.5 border border-white/20 text-white/70 hover:text-white hover:border-white/40 text-sm transition-all">
            Disable 2FA
          </button>
        )}

        {enabled && disableMode && (
          <div className="border border-white/10 p-4 bg-white/[0.02]">
            <p className="text-white/60 text-sm mb-3">Enter a current authenticator code (or a recovery code) to disable 2FA.</p>
            <div className="flex gap-3">
              <input data-testid="disable-2fa-code" value={disableCode} onChange={e => setDisableCode(e.target.value)} placeholder="123456" className="flex-1 bg-white/[0.03] border border-white/10 px-4 py-2.5 text-white text-sm tracking-widest placeholder:text-white/30 focus:outline-none" />
              <button data-testid="disable-2fa-confirm" onClick={disable2FA} className="px-5 py-2.5 bg-red-500/90 hover:bg-red-500 text-white text-sm">Confirm</button>
              <button onClick={() => { setDisableMode(false); setDisableCode(''); }} className="px-4 py-2.5 border border-white/20 text-white/60 text-sm">Cancel</button>
            </div>
          </div>
        )}

        {/* Setup flow */}
        {!enabled && !setup && (
          <button data-testid="enable-2fa-btn" onClick={startSetup} className="px-6 py-3 bg-white text-black text-sm font-medium hover:bg-white/90 transition-all">
            Enable 2FA
          </button>
        )}

        {!enabled && setup && (
          <div className="space-y-5" data-testid="2fa-setup">
            <ol className="text-white/60 text-sm space-y-1 list-decimal list-inside">
              <li>Open Google Authenticator (or Authy) on your phone.</li>
              <li>Tap “+” and scan the QR code below.</li>
              <li>Enter the 6-digit code it shows to confirm.</li>
            </ol>
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <img src={setup.qr_data_url} alt="2FA QR code" className="w-44 h-44 bg-white p-2" data-testid="2fa-qr" />
              <div className="flex-1">
                <p className="text-white/40 text-xs uppercase mb-1">Or enter this key manually</p>
                <code className="block text-white/80 text-sm break-all bg-white/[0.03] border border-white/10 p-3 font-mono">{setup.secret}</code>
                <div className="mt-4">
                  <label className="text-white/50 text-xs uppercase mb-2 block">Verification code</label>
                  <div className="flex gap-3">
                    <input data-testid="2fa-verify-code" value={code} onChange={e => setCode(e.target.value)} placeholder="123456" maxLength={6} className="w-40 bg-white/[0.03] border border-white/10 px-4 py-2.5 text-white text-base tracking-[0.4em] placeholder:text-white/30 focus:outline-none focus:border-white/30" />
                    <button data-testid="2fa-verify-btn" onClick={verifySetup} disabled={verifying} className="px-6 py-2.5 bg-white text-black text-sm font-medium disabled:opacity-50">
                      {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Enable'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Backup codes modal */}
      {backupCodes && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6" data-testid="backup-codes-modal">
          <div className="bg-[#0f0f0f] border border-white/10 max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2 text-amber-400">
                <AlertTriangle className="w-5 h-5" />
                <h3 className="text-white font-medium">Save your recovery codes</h3>
              </div>
              <button onClick={() => setBackupCodes(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-white/50 text-sm mb-4">Store these somewhere safe. Each code works once if you lose access to your authenticator app.</p>
            <div className="grid grid-cols-2 gap-2 bg-white/[0.03] border border-white/10 p-4 font-mono text-sm text-white/80 mb-4">
              {backupCodes.map((c, i) => <span key={i} data-testid={`backup-code-${i}`}>{c}</span>)}
            </div>
            <div className="flex gap-3">
              <button onClick={copyCodes} className="flex items-center gap-2 px-4 py-2.5 border border-white/20 text-white/70 hover:text-white text-sm">
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}{copied ? 'Copied' : 'Copy all'}
              </button>
              <button data-testid="backup-codes-done" onClick={() => setBackupCodes(null)} className="px-5 py-2.5 bg-white text-black text-sm font-medium">I've saved them</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityTab;
